import { Select } from 'antd'
import { AxoloTimePicker } from '../utils/axolo-timepicker'
import { AxoloDaySelector } from '../utils/day-selector'
import RefreshConversationsButton from '../utils/refreshConversationsButton'
import RefreshTeamButton from '../utils/refreshTeamsButton'
import { useState } from 'react'
import CreatableSelect from 'react-select/creatable'
import { components as selectComponents } from 'react-select'

const { Option } = Select

export const SelectTeamStandup = ({ teams, setTeamStandupSelect, teamStandupSelect }) => {
  const teamSelect = []
  teamSelect.push(
    <Option
      key={teams?.[0]?.organization || '1'}
      value="organization-wide"
      id={teams?.[0]?.organization || 'organization-wide'}
    >
      organization-wide
    </Option>,
  )
  teamSelect.push(
    <Option
      key={'personalReminder'}
      value="only me (direct message)"
      id={'personalReminder'}
    >
      only me (direct message)
    </Option>,
  )
  for (let i = 0; i < teams?.length; i++) {
    const team = teams[i]
    teamSelect.push(
      <Option key={team.id} value={team.name} id={team.id}>
        {team.name}
      </Option>,
    )
  }

  return (
    <div className="flex">
      <Select
        allowClear
        style={{ width: 200 }}
        placeholder="Select a team"
        value={teamStandupSelect?.value || undefined}
        labelInValue
        onChange={(e) => setTeamStandupSelect(e)}
        optionFilterProp="children"
        showSearch
        filterOption={(input, option) =>
          option.children.toLowerCase().includes(input.toLowerCase())
        }
      >
        {teamSelect}
      </Select>
      <RefreshTeamButton />
    </div>
  )
}

// Channel ID input option component
const ChannelIdInputOption = () => {
  return (
    <div className="px-3 py-2 text-sm text-gray-700 cursor-pointer hover:bg-gray-100">
      <div className="font-medium">Enter a channel ID or URL</div>
      <div className="text-xs text-gray-500">
        Paste a Slack channel ID or URL to add it directly
      </div>
    </div>
  )
}

export const StandupSelectChannels = ({
  slackChannels,
  setChannelSelected,
  channelSelected,
  teamStandupSelect,
  organizationId,
  user,
}) => {
  const [filteredOptions, setFilteredOptions] = useState([])
  const menuPortalTarget = typeof document !== 'undefined' ? document.body : null
  const selectStyles = {
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    menu: (base) => ({ ...base, zIndex: 9999 }),
  }
  
  // Parse channel input to detect if it's a channel ID or URL
  const parseChannelInput = (input) => {
    const trimmedInput = input.trim()
    
    // Check if it's a Slack URL (https://workspace.slack.com/archives/CHANNELID)
    const urlMatch = trimmedInput.match(/https:\/\/[^/]+\.slack\.com\/archives\/([A-Z0-9]+)/i)
    if (urlMatch) {
      return {
        channelId: urlMatch[1],
        isUrl: true,
        original: trimmedInput
      }
    }
    
    // Check if it's a direct channel ID
    if (/^[A-Z0-9]{9,11}$/i.test(trimmedInput)) {
      return {
        channelId: trimmedInput,
        isUrl: false,
        original: trimmedInput
      }
    }
    
    return null
  }

  // Fetch channel info by ID
  const fetchChannelById = async (channelId) => {
    try {
      const axios = user?.axios
      if (!axios) return null

      const { data } = await axios.post(
        `/conversations/channel/info`,
        {
          channelId,
          organizationId,
        },
        {
          skipAuthRedirect: true,
          validateStatus: (status) => status < 500,
        },
      )

      if (data && !data.error) {
        return data
      }

      return null
    } catch (error) {
      console.log('Error fetching channel info', error)
      return null
    }
  }

  // Format label for create option
  const formatLabel = (inputValue) => {
    const parsedChannel = parseChannelInput(inputValue)
    
    if (parsedChannel) {
      if (parsedChannel.isUrl) {
        return `Use ${parsedChannel.channelId} as a standup channel`
      } else {
        return `Use ${inputValue} as a standup channel`
      }
    }
    
    return `Create new channel "${inputValue}"`
  }

  // Handle title change
  const handleChannelChange = async (newValue) => {
    // Prevent selecting the channel ID input option
    if (newValue?.isChannelIdInput) {
      return
    }
    
    if (newValue?.label?.length > 0 && newValue?.__isNew__) {
      let { label } = newValue
      
      // Check if this looks like a channel ID or URL
      const originalLabel = label
      const parsedChannel = parseChannelInput(originalLabel)
       
      if (parsedChannel) {
        // This looks like a channel ID or URL, try to fetch channel info
        const response = await fetchChannelById(parsedChannel.channelId)
        if (response?.id || response?.data?.id) {
          const channelData = response?.data || response

          // Create channel option
          const channelOption = {
            key: channelData.id,
            label: channelData.name,
          }

          // Update channel selected
          setChannelSelected(channelOption)
          return
        }
      }
    }
    
    // Handle normal selection
    setChannelSelected(newValue)
  }

  // Custom Option component for react-select
  const CustomOption = (props) => {
    const { data, innerRef, innerProps } = props
    
    if (data.isChannelIdInput) {
      return (
        <div ref={innerRef} {...innerProps}>
          <ChannelIdInputOption />
        </div>
      )
    }
    
    // Default option rendering
    return <selectComponents.Option {...props} />
  }

  // Create channel options
  const channelSelectOptions = []
  const maxChannelsToShow = 10

  for (let i = 0; i < slackChannels?.length; i++) {
    const channel = slackChannels[i]
    channelSelectOptions.push({
      value: channel.title,
      label: channel.title,
      key: channel.communicationToolId,
    })
  }

  channelSelectOptions.push({
    value: "Direct message",
    label: "Direct message",
    key: user.authed_user,
  })

  // Get initial options
  const getInitialOptions = () => {
    const initial = channelSelectOptions.slice(0, maxChannelsToShow)
    if (initial.length <= 5) {
      initial.push({
        value: '__CHANNEL_ID_INPUT__',
        label: '__CHANNEL_ID_INPUT__',
        isChannelIdInput: true,
      })
    }
    return initial
  }

  // Filter options based on input
  const filterAndLimitOptions = (inputValue) => {
    const filtered = channelSelectOptions
      .filter((option) => option.label.toLowerCase().includes(inputValue.toLowerCase()))
      .slice(0, maxChannelsToShow)
    
    // Add custom channel ID input option if there are 5 or fewer results
    if (filtered.length <= 5) {
      filtered.push({
        value: '__CHANNEL_ID_INPUT__',
        label: '__CHANNEL_ID_INPUT__',
        isChannelIdInput: true,
      })
    }
    
    return filtered
  }

  let disabled = false
  // If I select a personal reminder, I can't change the channel
  if (teamStandupSelect?.key === 'personalReminder') {
    disabled = true
  }

  return (
    <div className="flex">
      <CreatableSelect
        createOptionPosition="first"
        isClearable
        components={{ Option: CustomOption }}
        menuPortalTarget={menuPortalTarget}
        menuPosition="fixed"
        styles={selectStyles}
        onInputChange={(newValue) => {
          // Check if the input looks like a channel ID or URL before processing
          const parsedChannel = parseChannelInput(newValue)
          
          if (parsedChannel) {
            // If it's a channel ID or URL, update the filtered options
            setFilteredOptions(filterAndLimitOptions(newValue))
            return
          }
          
          setFilteredOptions(filterAndLimitOptions(newValue))
        }}
        onChange={handleChannelChange}
        onBlur={(e) => {
          const inputValue = e.target.value
          if (inputValue) {
            const parsedChannel = parseChannelInput(inputValue)
            if (parsedChannel) {
              // Automatically process channel ID/URL when user clicks outside
              handleChannelChange({
                label: inputValue,
                value: inputValue,
                __isNew__: true,
              })
            }
          }
        }}
        openMenuOnClick={false}
        options={filteredOptions.length > 0 ? filteredOptions : getInitialOptions()}
        formatCreateLabel={formatLabel}
        value={channelSelected ? { label: channelSelected.label, value: channelSelected.label } : null}
        placeholder="Start typing or paste a channel ID"
        className="w-[280px]"
        noOptionsMessage={({ inputValue }) =>
          inputValue ? `Create a new channel "${inputValue}"` : 'Create a new channel'
        }
        isDisabled={disabled}
      />
      <RefreshConversationsButton />
    </div>
  )
}

export const StandupSelectTime = ({
  setTimeSelected,
  axios,
  daySelected,
  setDaySelected,
  timeSelected,
  isAdmin,
}) => {
  const format = 'HH:mm'

  async function onChangeTimePicker(time, timeString) {
    // Time is a moment object and timestring is formated
    await setTimeSelected(time)
  }

  function handleClickOnDay({ day }) {
    setDaySelected({ ...daySelected, [day]: !daySelected[day] })
  }

  return (
    <div className="">
      <AxoloTimePicker
        format={format}
        axios={axios}
        onChangeTimePicker={onChangeTimePicker}
        value={timeSelected}
        height={32}
        width={100}
        timeZoneWidth={150}
        shortTimezone={true}
        disabledTimeZone={!isAdmin}
      />
      <div className="absolute w-full">
        <AxoloDaySelector handleClickOnDay={handleClickOnDay} daySelected={daySelected} />
      </div>
    </div>
  )
}
